import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Clock, MapPin, Users, User, ChevronDown, Send, X, CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

const offerings = [
  {
    category: "Two Day Wananga",
    categoryColour: "#bb321a",
    icon: "group",
    items: [
      {
        title: "1. Kawanatanga: Governance Foundations",
        tagline: "For iwi, hapu and Maori trusts",
        description: "Practical, marae-tested governance training for trustees and board members of iwi authorities, post-settlement entities, marae committees and whanau trusts, built around the realities of governing in te ao Maori rather than an imported boardroom template. Good governance is the difference between an entity that protects and grows what its people have fought for, and one that quietly erodes it.",
        duration: "2 days",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Northland, Auckland, Bay of Plenty, Waikato, Wellington (nationwide by arrangement)",
        categories: "Governance, Leadership, Risk Management",
      },
      {
        title: "2. Business Fundamentals Wananga: Knowing Your Pakihi",
        tagline: "The full picture in one immersive wānanga",
        description: "A complete grounding in the fundamentals of running a pākihi: direction, structure, the numbers, growth strategy and a clear plan to act on. Built for owners who want the full picture in one immersive wānanga, rather than piecing it together one workshop at a time. Many business owners are excellent at their trade but were never taught the fundamentals of running the business itself. This two-day wānanga closes that gap in one sitting.",
        duration: "2 days",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Auckland, Bay of Plenty, Northland, Waikato, Wellington",
        categories: "Business Planning, Finance, Marketing Strategies",
      },
      {
        title: "3. Leadership Development: Growing Leaders for Iwi, Maori Lands Trusts and Pasifika Organisations",
        tagline: "Leadership that stays connected to the people you lead for",
        description: "Practical leadership development for emerging and current leaders across iwi entities, Māori Lands Trusts, hapū organisations, Pasifika trusts, community organisations, and any organisation where leadership carries accountability beyond the boardroom. Leadership in many community-centred organisations carries a different weight. A leader is accountable not only to a board or a budget, but to whānau, hapū, iwi, āiga and the wider community they serve. Co-designed by T.I.I.N.A.'s leadership team, including our Pasifika directors, so the content speaks authentically across cultural contexts.",
        duration: "2 days",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Northland, Auckland, Bay of Plenty, Waikato, Wellington",
        categories: "Leadership, Governance, People Management",
      },
    ],
  },
  {
    category: "One Day Group Teaching",
    categoryColour: "#364225",
    icon: "group",
    items: [
      {
        title: "4. Climate Ready Pakihi",
        tagline: "Risk, resilience and opportunity planning",
        description: "A grounded, practical group session helping business owners understand their climate and environmental risk exposure, build a resilience plan, and identify genuine commercial opportunity in the transition economy. Drawing on T.I.I.N.A.'s experience supporting marae and communities through extreme weather events, this is a business planning session grounded in the realities of operating in the regions of Aotearoa. Suitable for any business owner preparing for climate volatility, not a generic sustainability seminar.",
        duration: "1 day",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Bay of Plenty, East Coast, Northland, Gisborne",
        categories: "Risk Management, Business Planning, Environmental Sustainability",
      },
      {
        title: "5. Digital Foundations",
        tagline: "Building capability and data confidence for your pakihi",
        description: "Foundational digital capability training for business owners who want their data working for them: choosing the right tools, building a simple dashboard, and keeping the business safe online. Many small businesses are sitting on data they never use because the tools feel overwhelming or disconnected. This session strips that back to the essentials: what to track, what tool to use, and how to read what the numbers are actually saying. Open to all business owners regardless of sector or background.",
        duration: "1 day",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Auckland, Bay of Plenty, Northland, Waikato, Wellington",
        categories: "Digital, Business Systems and Processes",
      },
      {
        title: "6. Whanaungatanga in Business",
        tagline: "Relationship-led growth and strategic partnerships",
        description: "Training in relationship-led business development: mapping your network, building genuine partnerships, and growing through trust rather than transactions. In te ao Māori, business has always moved through relationships first, and Pasifika business carries the same understanding through the concept of vā. These principles apply across all business cultures. This session teaches relationship-led growth as a deliberate strategy, helping owners map their existing network, identify the strategic partners worth investing in, and build the kind of long-term relationships that generate referrals, contracts and collaboration.",
        duration: "1 day",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Northland, Auckland, Bay of Plenty, Wellington",
        categories: "Marketing Strategies, Business Growth, Business Planning",
      },
      {
        title: "7. Business Growth Essentials: Kia Tipu To Pakihi",
        tagline: "Pricing, cashflow, systems and the levers that drive growth",
        description: "A focused, one day session for business owners who have moved past start-up and are ready to grow. Growth exposes the gaps that survival hid. This session is built for any business owner who is already trading and wants a clear, honest look at what is holding growth back, whether that is pricing left too low, cashflow that is too tight, or systems that have not kept pace with demand. Participants work through their own numbers and operations during the day.",
        duration: "1 day",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Northland, Auckland, Bay of Plenty, Waikato, Wellington",
        categories: "Business Growth, Finance, Business Planning",
      },
      {
        title: "8. Construction and Infrastructure Health and Safety",
        tagline: "Site-ready, compliant and confident from day one",
        description: "Comprehensive health and safety training designed to get a workforce site-ready, compliant and confident from day one. This session works through the Health and Safety at Work Act 2015 in practical terms, covering site-specific hazard identification, risk management and incident reporting, so participants understand not just the rules but how to apply them on a live site. Suitable for any construction, civil, or infrastructure workforce. Delivered by practitioners with direct experience leading infrastructure and construction programmes.",
        duration: "1 day",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Northland, Auckland, Bay of Plenty, Waikato, Wellington",
        categories: "Health and Safety, Risk Management, Compliance",
      },
    ],
  },
  {
    category: "One on One Training",
    categoryColour: "#45443f",
    icon: "individual",
    items: [
      {
        title: "9. Wealth and Procurement Readiness",
        tagline: "Winning government and corporate contracts",
        description: "A tailored, one-to-one capability package for a Maori or Pacific business owner ready to compete for government and corporate procurement. Government and large corporates are actively seeking Maori and Pacific suppliers, but the procurement system rewards businesses that understand how it works. This package demystifies that system for one specific business, from registering on supplier panels through to pricing, writing and submitting a tender already in the business's pipeline.",
        duration: "1 day",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Northland, Auckland, Bay of Plenty, Wellington",
        categories: "Business Growth, Finance, Business Planning",
      },
      {
        title: "10. People and Capability",
        tagline: "Recruiting, retaining and growing your workforce",
        description: "Tailored, tikanga-informed people leadership coaching for a business owner or manager, covering recruitment, onboarding, performance and retention, shaped around the specific people challenges that business is facing right now. The approach is people-centred, treating staff as people to be developed rather than resources to be managed. Applicable to any organisation that values its workforce, regardless of sector.",
        duration: "1 day",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Northland, Auckland, Bay of Plenty, Waikato, Wellington",
        categories: "People Management, Leadership, Business Planning",
      },
      {
        title: "11. Economic Benefit and Procurement Intelligence",
        tagline: "Measuring, tracking and maximising economic benefit",
        description: "Tailored training in measuring, tracking and maximising the economic benefit of procurement activity, helping an organisation assess where value is created, retained and distributed across its supply chain. The package covers progressive procurement policy, social procurement frameworks and supplier diversity programmes, alongside practical economic impact measurement, so participants can build the evidence base that funders, councils and government agencies increasingly require. Relevant to any organisation seeking to understand and maximise the local economic impact of its procurement spend. Built on T.I.I.N.A.'s proprietary procurement intelligence platform.",
        duration: "1 day",
        priceType: "Per group, minimum 6 participants",
        delivery: "In person",
        locations: "Northland, Auckland, Bay of Plenty, Wellington",
        categories: "Business Growth, Finance, Business Planning",
      },
    ],
  },
];

export default function TrainingSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [expanded, setExpanded] = useState(null);
  const [booking, setBooking] = useState(null);
  const [bookForm, setBookForm] = useState({ name: "", email: "", phone: "", organisation: "", participants: "1", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [assessmentType, setAssessmentType] = useState(null);
  const [assessForm, setAssessForm] = useState({ name: "", business: "", email: "", wananga_location: "", q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, comment: "" });
  const [assessSubmitting, setAssessSubmitting] = useState(false);
  const [assessSubmitted, setAssessSubmitted] = useState(false);

  const assessQuestions = [
    { key: "q1", text: "How clearly can you articulate why your business exists and what makes it different?", framework: "Te Take, Aho Tuakiri" },
    { key: "q2", text: "How confident are you that your business structure, governance, and legal foundations are fit for purpose?", framework: "Aho Whakahaere, Whenu Ture" },
    { key: "q3", text: "How well do you understand your numbers: break-even, pricing, cashflow, and margins?", framework: "Whenu Pūtea" },
    { key: "q4", text: "How clear is your growth strategy and how deliberately are you investing in key relationships?", framework: "Aho Whakatinana, Aho Hononga" },
    { key: "q5", text: "How ready is your business to access funding, certification, and procurement opportunities?", framework: "Whenu Ingoa, Whenu Roa" },
  ];

  const submitAssessment = async () => {
    if (!assessForm.name || !assessForm.email) { toast.error("Name and email are required"); return; }
    if (!assessForm.wananga_location) { toast.error("Please select your training location"); return; }
    if (assessQuestions.some(q => assessForm[q.key] === 0)) { toast.error("Please answer all 5 questions"); return; }
    setAssessSubmitting(true);
    try {
      await axios.post(`${API}/training/assessment`, { ...assessForm, type: assessmentType });
      setAssessSubmitted(true);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setAssessSubmitting(false); }
  };

  const toggle = (key, title, category) => {
    if (expanded !== key) {
      // Track click
      axios.post(`${API}/training/click`, { title, category }).catch(() => {});
    }
    setExpanded(expanded === key ? null : key);
  };

  const openBooking = (title, category) => {
    setBooking({ title, category });
    setBookForm({ name: "", email: "", phone: "", organisation: "", participants: "1", message: "" });
  };

  const submitBooking = async () => {
    if (!bookForm.name || !bookForm.email) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/training/book`, {
        offering_title: booking.title,
        category: booking.category,
        ...bookForm,
        participants: parseInt(bookForm.participants) || 1,
      });
      toast.success("Enquiry submitted. We will be in touch.");
      setBooking(null);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <section
      id="training"
      data-testid="training-section"
      className="relative py-16 sm:py-24 overflow-hidden"
      style={{ backgroundColor: "#FDFBF7" }}
    >
      <div ref={ref} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-5"
        >
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#bb321a] mb-4">Our Training</p>
          <h2 className="font-['Syne'] text-4xl sm:text-5xl font-bold tracking-tight text-[#bb321a] leading-[1.05]">
            Building capability that <span className="text-[#364225]">lasts.</span>
          </h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="text-base sm:text-lg text-[#2D2A26] leading-relaxed max-w-4xl mb-14"
        >
          Our training programmes are evidence-based, professionally designed, and delivered by qualified practitioners with decades of experience across governance, business, economic development, and community leadership. Grounded in tikanga and structured for measurable outcomes, each programme builds genuine capability for Maori, Pasifika, and all communities and organisations committed to sustainable growth.
        </motion.p>

        {/* Offerings by category */}
        {offerings.map((cat, catIdx) => (
          <motion.div
            key={cat.category}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.2 + catIdx * 0.1 }}
            className="mb-12"
          >
            {/* Category header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center" style={{ background: cat.categoryColour }}>
                {cat.icon === "individual" ? <User className="w-5 h-5 text-white" /> : <Users className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h3 className="font-['Syne'] text-lg sm:text-xl font-bold text-[#bb321a]">{cat.category}</h3>
                <p className="text-[10px] text-stone-500 uppercase tracking-wider">{cat.items.length} programme{cat.items.length > 1 ? "s" : ""}</p>
              </div>
            </div>

            {/* Offering cards */}
            <div className="space-y-3">
              {cat.items.map((item, itemIdx) => {
                const key = `${catIdx}-${itemIdx}`;
                const isOpen = expanded === key;
                return (
                  <div
                    key={key}
                    className={`bg-white border transition-all duration-300 ${isOpen ? "border-[#bb321a] ring-1 ring-[#bb321a]/10" : "border-stone-200 hover:border-stone-300"}`}
                  >
                    {/* Colour strip */}
                    <div className="h-1" style={{ background: cat.categoryColour }} />

                    {/* Header (always visible) */}
                    <button
                      onClick={() => toggle(key, item.title, cat.category)}
                      className="w-full text-left px-5 sm:px-6 py-4 flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <h4 className="font-['Syne'] text-base sm:text-lg font-bold text-[#bb321a] leading-tight">{item.title}</h4>
                        <p className="text-xs sm:text-sm text-[#57534E] mt-1">{item.tagline}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                          <span className="text-[10px] text-stone-500 flex items-center gap-1"><Clock className="w-3 h-3" />{item.duration}</span>
                          <span className="text-[10px] text-stone-500 flex items-center gap-1">{cat.icon === "individual" ? <User className="w-3 h-3" /> : <Users className="w-3 h-3" />}{item.priceType}</span>
                          <span className="text-[10px] text-stone-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{item.delivery}</span>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-stone-400 flex-shrink-0 mt-1 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Expanded detail */}
                    <div className={`transition-all duration-500 overflow-hidden ${isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}>
                      <div className="px-5 sm:px-6 pb-6 pt-0 space-y-5">
                        {/* Description */}
                        <p className="text-sm text-[#44403C] leading-relaxed">{item.description}</p>

                        {/* Footer details */}
                        <div className="pt-4 border-t border-stone-100 flex flex-wrap items-end justify-between gap-4">
                          <div className="flex flex-wrap gap-x-6 gap-y-2">
                            <div>
                              <p className="text-[9px] font-bold text-stone-400 uppercase">Locations</p>
                              <p className="text-xs text-[#44403C]">{item.locations}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold text-stone-400 uppercase">Categories</p>
                              <p className="text-xs text-[#44403C]">{item.categories}</p>
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); openBooking(item.title, cat.category); }}
                            className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2 hover:opacity-90 transition-all flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, #bb321a, #364225)" }}
                          >
                            <Send className="w-3.5 h-3.5" />Enquire Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        ))}

        {/* Booking modal */}
        {booking && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setBooking(null)}>
            <div className="bg-white w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <div className="h-1" style={{ background: "linear-gradient(90deg, #bb321a, #364225)" }} />
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-[10px] font-bold text-[#bb321a] uppercase tracking-wider mb-1">Training Enquiry</p>
                    <h3 className="font-['Syne'] text-lg font-bold text-[#bb321a]">{booking.title}</h3>
                  </div>
                  <button onClick={() => setBooking(null)} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input value={bookForm.name} onChange={e => setBookForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name *" className="rounded-none" />
                    <Input type="email" value={bookForm.email} onChange={e => setBookForm(f => ({ ...f, email: e.target.value }))} placeholder="Email *" className="rounded-none" />
                    <Input value={bookForm.phone} onChange={e => setBookForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" className="rounded-none" />
                    <Input value={bookForm.organisation} onChange={e => setBookForm(f => ({ ...f, organisation: e.target.value }))} placeholder="Organisation" className="rounded-none" />
                    <Input type="number" min="1" value={bookForm.participants} onChange={e => setBookForm(f => ({ ...f, participants: e.target.value }))} placeholder="Number of participants" className="rounded-none" />
                  </div>
                  <Textarea value={bookForm.message} onChange={e => setBookForm(f => ({ ...f, message: e.target.value }))} placeholder="Any additional information or preferred dates" className="rounded-none" rows={3} />
                  <button
                    onClick={submitBooking}
                    disabled={submitting}
                    className="w-full py-3 text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg, #bb321a, #364225)" }}
                  >
                    <Send className="w-4 h-4" />{submitting ? "Submitting..." : "Submit Enquiry"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pre/Post Training Assessment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="mt-12"
        >
          <div className="h-px bg-gradient-to-r from-[#bb321a] via-[#485e23] to-[#364225] mb-8" />
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#364225] mb-3">Participant Assessment</p>
          <h3 className="font-['Syne'] text-2xl sm:text-3xl font-bold tracking-tight text-[#bb321a] leading-[1.05] mb-2">
            Measure your <span className="text-[#bb321a]">progress.</span>
          </h3>
          <p className="text-sm text-[#57534E] mb-6 max-w-2xl">If you are attending a T.I.I.N.A. wānanga, please complete the pre-training assessment before you arrive and the post-training assessment at the end of the programme.</p>

          {!assessmentType && !assessSubmitted && (
            <div className="flex gap-4">
              <button onClick={() => { setAssessmentType("pre"); setAssessSubmitted(false); setAssessForm({ name: "", business: "", email: "", q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, comment: "" }); }}
                className="px-6 py-4 text-sm font-bold uppercase tracking-wider text-white hover:opacity-90 transition-all"
                style={{ background: "linear-gradient(135deg, #bb321a, #485e23)" }}>
                Pre-Training Assessment
              </button>
              <button onClick={() => { setAssessmentType("post"); setAssessSubmitted(false); setAssessForm({ name: "", business: "", email: "", wananga_location: "", q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, comment: "" }); }}
                className="px-6 py-4 text-sm font-bold uppercase tracking-wider text-white hover:opacity-90 transition-all"
                style={{ background: "linear-gradient(135deg, #364225, #45443f)" }}>
                Post-Training Assessment
              </button>
            </div>
          )}

          {assessSubmitted && (
            <div className="bg-white border border-[#364225] p-8 text-center max-w-lg">
              <CheckCircle2 className="w-10 h-10 text-[#364225] mx-auto mb-3" />
              <p className="font-['Syne'] text-lg font-bold text-[#bb321a] mb-2">
                {assessmentType === "pre" ? "Pre-Training Assessment Submitted" : "Post-Training Assessment Submitted"}
              </p>
              <p className="text-sm text-[#57534E] mb-4">Thank you. Your responses have been recorded.</p>
              <button onClick={() => { setAssessmentType(null); setAssessSubmitted(false); }}
                className="text-xs font-bold text-[#bb321a] uppercase tracking-wider">Back to Assessments</button>
            </div>
          )}

          {assessmentType && !assessSubmitted && (
            <div className="bg-white border border-stone-200 max-w-3xl">
              <div className="h-1" style={{ background: assessmentType === "pre" ? "#bb321a" : "#364225" }} />
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: assessmentType === "pre" ? "#bb321a" : "#364225" }}>
                      {assessmentType === "pre" ? "Pre-Training" : "Post-Training"} Assessment
                    </p>
                    <h4 className="font-['Syne'] text-lg font-bold text-[#bb321a]">Business Fundamentals Wānanga</h4>
                  </div>
                  <button onClick={() => setAssessmentType(null)} className="text-xs text-stone-400 hover:text-stone-600">Cancel</button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div><label className="text-[10px] font-bold text-[#44403C] uppercase tracking-wider block mb-1">Your Name *</label>
                    <Input value={assessForm.name} onChange={e => setAssessForm(f => ({ ...f, name: e.target.value }))} className="rounded-none" /></div>
                  <div><label className="text-[10px] font-bold text-[#44403C] uppercase tracking-wider block mb-1">Business Name</label>
                    <Input value={assessForm.business} onChange={e => setAssessForm(f => ({ ...f, business: e.target.value }))} className="rounded-none" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  <div><label className="text-[10px] font-bold text-[#44403C] uppercase tracking-wider block mb-1">Email *</label>
                    <Input type="email" value={assessForm.email} onChange={e => setAssessForm(f => ({ ...f, email: e.target.value }))} className="rounded-none" /></div>
                  <div><label className="text-[10px] font-bold text-[#44403C] uppercase tracking-wider block mb-1">Training Location *</label>
                    <select value={assessForm.wananga_location} onChange={e => setAssessForm(f => ({ ...f, wananga_location: e.target.value }))} className="w-full h-10 border border-stone-200 px-3 text-sm">
                      <option value="">Select your location</option>
                      <option value="Te Kaha">Te Kaha</option>
                      <option value="Kawerau">Kawerau</option>
                      <option value="Whakat&#x101;ne">Whakat&#x101;ne</option>
                      <option value="Rotorua">Rotorua</option>
                      <option value="&#x14c;p&#x14d;tiki">&#x14c;p&#x14d;tiki</option>
                      <option value="Whang&#x101;rei">Whang&#x101;rei</option>
                      <option value="Tauranga">Tauranga</option>
                    </select></div>
                </div>

                <p className="text-xs text-[#57534E] mb-4">Rate your confidence in each area. 1 = Not at all, 5 = Very confident.</p>

                <div className="space-y-4">
                  {assessQuestions.map((q, i) => (
                    <div key={q.key} className="border-b border-stone-100 pb-3 last:border-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm text-[#bb321a] font-medium">{i + 1}. {q.text}</p>
                          <p className="text-[9px] text-[#bb321a] font-semibold mt-0.5">{q.framework}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {[1, 2, 3, 4, 5].map(n => (
                            <button key={n} onClick={() => setAssessForm(f => ({ ...f, [q.key]: n }))} type="button"
                              className={`w-9 h-9 text-sm font-bold transition-all ${assessForm[q.key] === n ? "bg-[#bb321a] text-white" : "bg-stone-100 text-stone-400 hover:bg-stone-200"}`}>
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4">
                  <label className="text-[10px] font-bold text-[#44403C] uppercase tracking-wider block mb-1">
                    {assessmentType === "pre" ? "What do you want us to know before we start?" : "What do you want to tell us now that the wānanga is complete?"}
                  </label>
                  <Textarea value={assessForm.comment} onChange={e => setAssessForm(f => ({ ...f, comment: e.target.value }))}
                    placeholder={assessmentType === "pre" ? "Anything you would like us to know about you, your business, or your expectations" : "Your reflections, feedback, or anything else you would like to share"}
                    className="rounded-none" rows={3} />
                </div>

                <button onClick={submitAssessment} disabled={assessSubmitting}
                  className="w-full mt-5 py-3 text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: assessmentType === "pre" ? "linear-gradient(135deg, #bb321a, #485e23)" : "linear-gradient(135deg, #364225, #45443f)" }}>
                  {assessSubmitting ? "Submitting..." : "Submit Assessment"}
                </button>
              </div>
            </div>
          )}
        </motion.div>

      </div>
    </section>
  );
}
